import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../components/Modal";
import Button from "../../../../components/Button";
import { Edit as EditInterface } from "../../../../interfaces/reservation.interface";
import { formatAmount, formatDollarAmount, formatNumber } from "../../../../utils/methods";
import clsx from "clsx";

interface CalculationDetailModalProps {
  open: boolean;
  onClose: () => void;
  formData: EditInterface;
  type: 'hostPayout' | 'ownerRevenue' | 'coHostCommission' | 'clientRevenue';
}

const CalculationDetailModal: React.FC<CalculationDetailModalProps> = ({
  open,
  onClose,
  formData,
  type,
}) => {
  const { t } = useTranslation();
  const platformCommission = formData.platformCommission;

  const getCalculationDetails = () => {
    const xNightRoomFees = Number(formData.xNightRoomFees) || 0;
    const hostFees = Number(formData.hostFees) || 0;
    const discount = Number(formData.discount) || 0;
    const cleaningFees = Number(formData.cleaningFees) || 0;
    const linenFees = Number(formData.linenFees) || 0;
    const petFees = Number(formData.petFees) || 0;
    const hstTax = Number(formData.hstTax) || 0;
    const matTax = Number(formData.matTax) || 0;
    const adjustments = Number(formData.adjustments) || 0;
    const includeAdjustments = formData.includeAdjustments ?? false;
    const hostPayout = Number(formData.hostPayout) || 0;
    const ownerRevenue = Number(formData.ownerRevenue) || 0;
    const commission = Number(formData.commission) || 0;
    const coHostCommission = Number(formData.coHostCommission) || 0;
    const clientRevenue = Number(formData.clientRevenue) || 0;
    

    switch (type) {
      case 'hostPayout':
        return {
          title: t("reservations.edit.pageTexts.form.stats.hostPayment"),
          steps: [
            { label: t("reservations.edit.pageTexts.form.xNightRoomFees.label"), value: formatDollarAmount(formatAmount(xNightRoomFees.toString())) },
            { label: `+ ${t("reservations.edit.pageTexts.form.cleaningFees.label")}`, value: formatDollarAmount(formatAmount(cleaningFees.toString())) },
            { label: `+ ${t("reservations.edit.pageTexts.form.linenFees.label")}`, value: formatDollarAmount(formatAmount(linenFees.toString())) },
            { label: `+ ${t("reservations.edit.pageTexts.form.petFees.label")}`, value: formatDollarAmount(formatAmount(petFees.toString())) },
            { label: `+ ${t("reservations.edit.pageTexts.form.hstTax.label")}`, value: formatDollarAmount(formatAmount(hstTax.toString())) },
            { label: `+ ${t("reservations.edit.pageTexts.form.matTax.label")}`, value: formatDollarAmount(formatAmount(matTax.toString())) },
            ...(includeAdjustments ? [{ label: adjustments < 0 ? `- ${t("reservations.edit.pageTexts.form.adjustments.label")}` : `+ ${t("reservations.edit.pageTexts.form.adjustments.label")}`, value: formatDollarAmount(formatAmount(Math.abs(adjustments).toString())) }] : []),
            { label: `- ${t("reservations.edit.pageTexts.form.hostFees.label")}`, value: formatDollarAmount(formatAmount(hostFees.toString())) },
            { label: `- ${t("reservations.edit.pageTexts.form.discount.label")}`, value: formatDollarAmount(formatAmount(discount.toString())) },
            { label: t("reservations.edit.pageTexts.form.stats.hostPayment"), value: formatDollarAmount(formatAmount(hostPayout.toString())), isTotal: true },
          ],
        };
      case 'ownerRevenue':
        if (!platformCommission) {
          return {
            title: t("reservations.edit.pageTexts.form.stats.ownerRevenue"),
            steps: [
              { label: t("reservations.edit.pageTexts.form.stats.hostPayment"), value: formatDollarAmount(formatAmount(hostPayout.toString())) },
              { label: `- ${t("reservations.edit.pageTexts.form.cleaningFees.label")}`, value: formatDollarAmount(formatAmount(cleaningFees.toString())) },
              { label: `- ${t("reservations.edit.pageTexts.form.linenFees.label")}`, value: formatDollarAmount(formatAmount(linenFees.toString())) },
              { label: `- ${t("reservations.edit.pageTexts.form.hstTax.label")}`, value: formatDollarAmount(formatAmount(hstTax.toString())) },
              { label: `- ${t("reservations.edit.pageTexts.form.matTax.label")}`, value: formatDollarAmount(formatAmount(matTax.toString())) },
              { label: t("reservations.edit.pageTexts.form.stats.ownerRevenue"), value: formatDollarAmount(formatAmount(ownerRevenue.toString())), isTotal: true },
            ],
          };
        }
        const platformCommissionCleaning = cleaningFees * (Number(platformCommission) / 100);
        const platformCommissionLinen = linenFees * (Number(platformCommission) / 100);
        
        const netCleaningFee = cleaningFees - platformCommissionCleaning;
        const netLinenFee = linenFees - platformCommissionLinen;
        
        const steps: any[] = [
          { label: t("reservations.edit.pageTexts.form.stats.hostPayment"), value: formatDollarAmount(formatAmount(hostPayout.toString())) },
        ];

        if (platformCommission !== null && platformCommission !== undefined) {
          steps.push(
            { 
              label: `- ${t("reservations.edit.pageTexts.form.calculationModal.net")} ${t("reservations.edit.pageTexts.form.cleaningFees.label")}`, 
              value: formatDollarAmount(formatAmount(netCleaningFee.toString())),
              calculationDetail: `${t("reservations.edit.pageTexts.form.cleaningFees.label").toLowerCase()} - (${t("reservations.edit.pageTexts.form.cleaningFees.label").toLowerCase()} × ${t("reservations.edit.pageTexts.form.stats.platformCommission").toLowerCase()}(${Number(platformCommission).toFixed(2)}%))`
            },
            { 
              label: `- ${t("reservations.edit.pageTexts.form.calculationModal.net")} ${t("reservations.edit.pageTexts.form.linenFees.label")}`, 
              value: formatDollarAmount(formatAmount(netLinenFee.toString())),
              calculationDetail: `${t("reservations.edit.pageTexts.form.linenFees.label").toLowerCase()} - (${t("reservations.edit.pageTexts.form.linenFees.label").toLowerCase()} × ${t("reservations.edit.pageTexts.form.stats.platformCommission").toLowerCase()}(${Number(platformCommission).toFixed(2)}%))`
            },
            { label: `- ${t("reservations.edit.pageTexts.form.hstTax.label")}`, value: formatDollarAmount(formatAmount(hstTax.toString())) },
            { label: `- ${t("reservations.edit.pageTexts.form.matTax.label")}`, value: formatDollarAmount(formatAmount(matTax.toString())) }
          );
        } else {
          steps.push(
            { label: `- ${t("reservations.edit.pageTexts.form.cleaningFees.label")}`, value: formatDollarAmount(formatAmount(cleaningFees.toString())) },
            { label: `- ${t("reservations.edit.pageTexts.form.linenFees.label")}`, value: formatDollarAmount(formatAmount(linenFees.toString())) },
            { label: `- ${t("reservations.edit.pageTexts.form.hstTax.label")}`, value: formatDollarAmount(formatAmount(hstTax.toString())) },
            { label: `- ${t("reservations.edit.pageTexts.form.matTax.label")}`, value: formatDollarAmount(formatAmount(matTax.toString())) }
          );
        }

        steps.push({ label: t("reservations.edit.pageTexts.form.stats.ownerRevenue"), value: formatDollarAmount(formatAmount(ownerRevenue.toString())), isTotal: true });

        return {
          title: t("reservations.edit.pageTexts.form.stats.ownerRevenue"),
          steps,
        };
      case 'coHostCommission':
        return {
          title: t("reservations.edit.pageTexts.form.stats.coHostCommission"),
          steps: [
            { 
              label: `${t("reservations.edit.pageTexts.form.stats.commission")} ${t("reservations.edit.pageTexts.form.calculationModal.commission")}`, 
              value: formatDollarAmount(formatAmount(coHostCommission.toString())),
              calculationDetail: `${t("reservations.edit.pageTexts.form.stats.ownerRevenue")} × ${t("reservations.edit.pageTexts.form.stats.commission")} ${t("reservations.edit.pageTexts.form.calculationModal.commission")}(${formatNumber(commission)}%)`
            },
            { label: t("reservations.edit.pageTexts.form.stats.coHostCommission"), value: formatDollarAmount(formatAmount(coHostCommission.toString())), isTotal: true },
          ],
        };
      case 'clientRevenue':
        return {
          title: t("reservations.edit.pageTexts.form.stats.clientRevenue"),
          steps: [
            { label: t("reservations.edit.pageTexts.form.stats.ownerRevenue"), value: formatDollarAmount(formatAmount(ownerRevenue.toString())) },
            { label: `- ${t("reservations.edit.pageTexts.form.stats.coHostCommission")}`, value: formatDollarAmount(formatAmount(coHostCommission.toString())) },
            { label: t("reservations.edit.pageTexts.form.stats.clientRevenue"), value: formatDollarAmount(formatAmount(clientRevenue.toString())), isTotal: true },
          ],
        };
      default:
        return { title: '', steps: [] };
    }
  };

  const { title, steps } = getCalculationDetails();

  const stepsWithRunningTotals = steps.map((step, index) => {
    if (step.isTotal) {
      return { ...step, runningTotal: null };
    }
    
    const absoluteValue = parseFloat(step.value.replace(/[$,]/g, '')) || 0;
    
    const isNegative = step.label.trim().startsWith('-');
    const numericValue = isNegative ? -absoluteValue : absoluteValue;
    
    let runningTotal = 0;
    for (let i = 0; i <= index; i++) {
      if (steps[i].isTotal) break;
      const absValue = parseFloat(steps[i].value.replace(/[$,]/g, '')) || 0;
      const isNeg = steps[i].label.trim().startsWith('-');
      runningTotal += isNeg ? -absValue : absValue;
    }
    
    return {
      ...step,
      numericValue,
      runningTotal: runningTotal.toFixed(2),
    };
  });

  const modalAction = (
    <Button
      className="btn btn-outline-secondary"
      text={t('common.errorMessages.unHandledRejectionPage.closeBtn')}
      onClick={onClose}
    />
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      titleClassName="text-start"
      content={
        <div className="d-flex flex-column gap-2">
          {stepsWithRunningTotals.map((step, index) => {
            const isTotal = step.isTotal;
            
            if (isTotal) {
              return (
                <div key={index}>
                  <div 
                    className="p-3 rounded"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="m-0 fw-bold">{step.label}</h5>
                      <h5 className="m-0 fw-bold">{step.value}</h5>
                    </div>
                  </div>
                </div>
              );
            }
            
            const numericValue = 'numericValue' in step ? step.numericValue : 0;
            const isDeduction = step.label.trim().startsWith('-');
            const isNegativeValue = numericValue < 0;
            const isNegative = isDeduction || isNegativeValue;
            const cleanLabel = step.label.replace(/^[+-]\s*/, '').trim();
            const displayValue = isNegative ? `- ${step.value}` : `+ ${step.value}`;
            
            return (
              <div key={index}>
                <div 
                  className="p-3 rounded"
                  style={{
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <span className="fw-bold text-dark">{cleanLabel}</span>
                    <span 
                      className={clsx("fw-bold", isNegative && 'text-danger')}
                    >
                      {displayValue}
                    </span>
                  </div>
                </div>
                {('runningTotal' in step && step.runningTotal !== null) || ('calculationDetail' in step && step.calculationDetail) ? (
                  <div className="d-flex justify-content-between mt-1">
                    {'calculationDetail' in step && step.calculationDetail ? (
                      <div className="text-muted small">
                        {step.calculationDetail}
                      </div>
                    ) : (
                      <span></span>
                    )}
                    {'runningTotal' in step && step.runningTotal !== null && (
                      <div className="text-muted small">
                        {t("reservations.edit.pageTexts.form.calculationModal.runningTotal")}: {formatDollarAmount(formatAmount(step.runningTotal))}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      }
      action={modalAction}
      actionClassName="justify-content-start"
      maxWidth="sm"
    />
  );
};

export default CalculationDetailModal;

